import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, ArrowRight, CheckCircle, Users, Target, Zap, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { trackFormSubmit } from '../utils/analytics';
import { SEO } from '../components/SEO';
import { BACKEND_URL } from '../lib/apiBase';

const problems = [
  'A csapat kommunikációja akadozik, félreértések vannak',
  'Az értékesítési eredmények stagnálnak',
  'Magas a fluktuáció, az emberek kiégnek',
  'A vezetők nem tudják motiválni a csapatot',
  'A konfliktusokat söprik a szőnyeg alá',
];

const solutions = [
  {
    icon: Users,
    title: 'Csapatkommunikáció',
    desc: 'Közös nyelv, tiszta elvárások, hatékony megbeszélések.',
  },
  {
    icon: Target,
    title: 'Értékesítési tréning',
    desc: 'Tárgyalástechnika, ügyfélkezelés, lezárási módszerek.',
  },
  {
    icon: Zap,
    title: 'Vezetői fejlesztés',
    desc: 'Delegálás, motiváció, visszajelzés-kultúra.',
  },
  {
    icon: BarChart3,
    title: 'Teljesítménymenedzsment',
    desc: 'Célkitűzés, mérés, fejlesztő beszélgetések.',
  },
];

export const B2BPage = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    team_size: '',
    challenge: '',
    message: '',
    privacy: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/b2b-inquiry`, formData);
      trackFormSubmit('b2b_inquiry', { team_size: formData.team_size, challenge: formData.challenge });
      toast.success('Ajánlatkérés elküldve! 24 órán belül felvesszük Önnel a kapcsolatot.');
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        team_size: '',
        challenge: '',
        message: '',
        privacy: false,
      });
    } catch (error) {
      toast.error('Hiba történt. Kérjük, próbálja újra.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="b2b-page">
      <SEO
        title="Céges tréningek"
        description="Testreszabott céges fejlesztési programok: csapatkommunikáció, értékesítési tréning, vezetői fejlesztés. Kérjen egyedi ajánlatot!"
        path="/b2b"
      />
      {/* Hero */}
      <section className="relative min-h-[50vh] md:min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img loading="lazy"
            src="https://images.unsplash.com/photo-1770943558988-2c605d6bd5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwd29ya3Nob3AlMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlJTIwZ2xhc3N8ZW58MHx8fHwxNzcyMTEzMDk5fDA&ixlib=rb-4.1.0&q=85"
            alt="Céges tréning workshop vállalati csapatnak — Az Akadémia"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#404041]/70 via-[#404041]/50 to-[#404041]" />
        </div>

        <div className="container-custom relative z-10 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Céges tréningek és vezetői fejlesztés
            </h1>
            <p className="text-xl text-white/70 mb-8">
              Testreszabott fejlesztési programok, amelyek valódi változást hoznak. 
              Nem dobozos megoldások, hanem a te kihívásaidra épülő tréningek.
            </p>
            <a
              href="#ajanlatkerem"
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="b2b-cta-inquiry"
            >
              Ajánlatkérés
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section className="section-padding bg-[#404041]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                Ismerős a helyzet?
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Minden csapatnak vannak kihívásai. A kérdés az, hogy ezek húzzák vissza 
                a teljesítményt, vagy felismered és kezeled őket.
              </p>
              <ul className="space-y-4">
                {problems.map((problem, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle size={20} className="text-brand-light-green flex-shrink-0 mt-1" />
                    <span className="text-white/70">{problem}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              className="glass-card p-8"
            >
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                A mi megközelítésünk
              </h3>
              <p className="text-white/60 mb-6">
                Nem hisszük, hogy létezik univerzális megoldás. Minden tréning előtt 
                felméréssel kezdünk, és a programot a valódi kihívásokra szabjuk.
              </p>
              <div className="space-y-4 text-sm text-white/50">
                <p>1. Helyzetfelmérés és célkitűzés</p>
                <p>2. Testreszabott program tervezése</p>
                <p>3. Interaktív tréning, valós szituációk</p>
                <p>4. Utánkövetés és mérhető eredmények</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="section-padding bg-[#05435a]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Területek, ahol segítünk
            </h2>
            <p className="text-white/60">
              Leggyakoribb tréningjeink — de minden program egyedi, a te igényeid alapján.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {solutions.map((solution, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ delay: idx * 0.1 }}
                className="training-card text-center"
              >
                <solution.icon size={32} className="text-brand-light-green mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  {solution.title}
                </h3>
                <p className="text-white/60 text-sm">{solution.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="ajanlatkerem" className="section-padding bg-[#404041]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                Kérj egyedi ajánlatot
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Töltsd ki az űrlapot, és 24 órán belül felvesszük veled a kapcsolatot. 
                Az első konzultáció ingyenes és kötelezettségmentes.
              </p>
              <div className="glass-card p-6">
                <h4 className="text-white font-semibold mb-4">Miért válassz minket?</h4>
                <ul className="space-y-3 text-white/60 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-brand-light-green" />
                    20+ év tapasztalat céges fejlesztésben
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-brand-light-green" />
                    150+ elégedett vállalati partner
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-brand-light-green" />
                    Mindig testreszabott programok
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-brand-light-green" />
                    Mérhető eredmények, utánkövetés
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
            >
              <form onSubmit={handleSubmit} className="glass-card p-5 md:p-8" data-testid="b2b-inquiry-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="text"
                      placeholder="Cégnév *"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="input-underline"
                      data-testid="b2b-input-company"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Kapcsolattartó neve *"
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="input-underline"
                      data-testid="b2b-input-contact"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Email cím *"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-underline"
                      data-testid="b2b-input-email"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      placeholder="Telefonszám *"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-underline"
                      data-testid="b2b-input-phone"
                    />
                  </div>
                  <div>
                    <select
                      value={formData.team_size}
                      onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
                      className="input-underline bg-transparent"
                      data-testid="b2b-input-teamsize"
                    >
                      <option value="" className="bg-[#404041]">Csapatméret</option>
                      <option value="5-10" className="bg-[#404041]">5-10 fő</option>
                      <option value="11-30" className="bg-[#404041]">11-30 fő</option>
                      <option value="31-50" className="bg-[#404041]">31-50 fő</option>
                      <option value="50+" className="bg-[#404041]">50+ fő</option>
                    </select>
                  </div>
                  <div>
                    <select
                      value={formData.challenge}
                      onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                      className="input-underline bg-transparent"
                      data-testid="b2b-input-challenge"
                    >
                      <option value="" className="bg-[#404041]">Fő kihívás</option>
                      <option value="communication" className="bg-[#404041]">Kommunikáció</option>
                      <option value="sales" className="bg-[#404041]">Értékesítés</option>
                      <option value="leadership" className="bg-[#404041]">Vezetés</option>
                      <option value="team" className="bg-[#404041]">Csapatmunka</option>
                      <option value="other" className="bg-[#404041]">Egyéb</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <textarea
                    placeholder="Írja le röviden a helyzetet és a céljait..."
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="input-underline resize-none"
                    data-testid="b2b-input-message"
                  />
                </div>
                <div className="mt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={formData.privacy}
                      onChange={(e) => setFormData({ ...formData, privacy: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-[#FBAF3F] flex-shrink-0"
                      data-testid="b2b-input-privacy"
                    />
                    <span className="text-white/60 text-sm leading-relaxed">
                      Elolvastam és elfogadom az{' '}
                      <Link to="/adatvedelem" target="_blank" className="text-[#56999B] hover:text-[#C4DB72] underline transition-colors">
                        Adatkezelési tájékoztatót
                      </Link>
                      , és hozzájárulok személyes adataim kezeléséhez. *
                    </span>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full mt-6 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="b2b-submit-btn"
                >
                  {loading ? 'Küldés...' : 'Ajánlatkérés elküldése'}
                  <Send size={18} />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default B2BPage;
